import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import { injectIntl, useIntl } from 'react-intl';

import { fetchCustomFields, fetchMetadataGroups } from '../../actions/Metadata';

import { APIupdateCustomFieldsOrder, APIupdateMetadataGroupOrder } from '../../api/Metadata';

import AdminCustomFieldsHeader from '../../components/Admin/CustomFields/Header';
import AdminCustomFieldsList from '../../components/Admin/CustomFields/List';
import EmptyCustomFields from '../EmptyContainers/CustomFields';

import { arrayMove } from '../../helpers';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { userHasFeature } from '../../helpers/Features';

import Loading from '../../components/Loading';
import ErrorBoundary from '../../components/ErrorBoundary';
import TrialNotice from '../../components/TrialNotice';
import UpgradeBoard from '../../components/UpgradeBoard';

const AdminCustomFields = ({
  customFields: propsCustomFields,
  metadataGroups: propsMetadataGroups,
  loadMetadataGroups,
  loadCustomFields,
}) => {
  const [customFields, setLocalCustomFields] = useState([]);
  const [metadataGroups, setMetadataGroups] = useState([]);
  const [groupOrders, setGroupOrders] = useState({});
  const [loading, setLoading] = useState(true);

  const intl = useIntl();

  useEffect(() => {
    loadMetadataGroups();
    loadCustomFields();
  }, [loadCustomFields, loadMetadataGroups]);

  useEffect(() => {
    setLocalCustomFields(propsCustomFields);
    setLoading(false);

    setGroupOrders(prev => ({
      ...prev,
      ungrouped: propsCustomFields.filter(cf => cf.metadata_groups?.length === 0).map(t => t.id),
      nogroups: propsCustomFields.map(cf => cf.id),
    }));
  }, [propsCustomFields]);

  useEffect(() => {
    setMetadataGroups(propsMetadataGroups);
    setLoading(false);

    setGroupOrders(prev => {
      const orders = { ...prev };
      propsMetadataGroups.forEach(metadataGroup => {
        orders[metadataGroup.id] = [...metadataGroup.custom_fields];
      });
      return orders;
    });
  }, [propsMetadataGroups]);

  const deleteCustomFieldFromList = useCallback((customField) => {
    setLocalCustomFields(prev => prev.filter(cf => cf.id !== customField.id));
    loadCustomFields();
  }, [loadCustomFields]);

  const moveCustomField = useCallback((dragId, hoverId, groupKey) => {
    setGroupOrders(prev => {
      const order = [...(prev[groupKey] || [])];
      const fmi = order.indexOf(dragId);
      const smi = order.indexOf(hoverId);
      if (fmi < 0 || smi < 0) return prev;
      const newOrder = arrayMove(order, fmi, smi);
      return { ...prev, [groupKey]: newOrder };
    });
  }, []);

  const saveCustomFields = useCallback((metadataGroupId, groupKey) => {
    setGroupOrders(prev => {
      const order = prev[groupKey] || [];
      const request = metadataGroupId
        ? APIupdateMetadataGroupOrder(metadataGroupId, { custom_fields: order })
        : APIupdateCustomFieldsOrder(order);

      request.then(() => {
        loadMetadataGroups();
        loadCustomFields();
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Tag Categories order saved' }),
        });
      }).catch(() => {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
        });
      });

      return prev;
    });
  }, [loadCustomFields, loadMetadataGroups, intl]);

  const customFieldsWithoutGroups = metadataGroups.length > 0 && customFields.length > 0
    ? (groupOrders.ungrouped || []).map(
      id => customFields.find(cf => cf.id === id),
    ).filter(Boolean)
    : [];

  if (!userHasFeature('custom-fields')) {
    return (
      <div className="main-content custom-fields-content">
        <Container fluid>
          <UpgradeBoard feature="custom-fields" />
        </Container>
      </div>
    );
  }

  return (
    <div className="main-content custom-fields-content">
      <Container fluid>
        <AdminCustomFieldsHeader />
        <TrialNotice feature="custom-fields" className="pl-0" />
        <p>
          {intl.formatMessage({ defaultMessage: 'Create and manage Custom Fields ' })}
          {/*
            TODO Change to custom fields
            <HelpIcon
              id="help-custom-categories"
              url="https://help.stockpress.co/en/articles/5937851-creating-and-managing-custom-categories"
            />
          */}
        </p>
        {loading ? (
          <Loading />
        ) : (
          <ErrorBoundary>
            {customFields.length === 0 && (
              <EmptyCustomFields link="/admin/settings/custom-fields/add" />
            )}
            {(metadataGroups.length === 0 && customFields.length > 0) && (
              <AdminCustomFieldsList
                customFields={
                  (groupOrders.nogroups || [])
                    .map(id => customFields.find(cf => cf.id === id))
                    .filter(Boolean)
                }
                deleteCustomFieldFromList={deleteCustomFieldFromList}
                moveCustomField={(dragId, hoverId) => moveCustomField(dragId, hoverId, 'nogroups')}
                saveCustomFields={() => saveCustomFields(undefined, 'nogroups')}
              />
            )}
            {(metadataGroups.length > 0 && customFields.length > 0) && (
              metadataGroups.map((metadataGroup) => {
                const order = groupOrders[metadataGroup.id] || metadataGroup.custom_fields;
                const groupCustomFields = order
                  .map(id => customFields.find(cf => cf.id === id))
                  .filter(Boolean);

                return (
                  <AdminCustomFieldsList
                    key={metadataGroup.id}
                    metadataGroup={metadataGroup}
                    customFields={groupCustomFields}
                    deleteCustomFieldFromList={deleteCustomFieldFromList}
                    moveCustomField={
                      (dragId, hoverId) => moveCustomField(dragId, hoverId, metadataGroup.id)
                    }
                    saveCustomFields={
                      () => saveCustomFields(metadataGroup.id, metadataGroup.id)
                    }
                  />
                );
              }))}

            {customFieldsWithoutGroups.length > 0 && (
              <AdminCustomFieldsList
                metadataGroup={{ id: null, name: 'No Group assigned', custom_fields: customFieldsWithoutGroups }}
                customFields={customFieldsWithoutGroups}
                customFieldsTotal={customFieldsWithoutGroups.length}
                deleteCustomFieldFromList={deleteCustomFieldFromList}
                moveCustomField={(dragId, hoverId) => moveCustomField(dragId, hoverId, 'ungrouped')}
                saveCustomFields={() => saveCustomFields(undefined, 'ungrouped')}
              />
            )}
          </ErrorBoundary>
        )}
      </Container>
    </div>
  );
};

const mapStateToProps = state => ({
  customFields: state.Metadata.customFields,
  metadataGroups: state.Metadata.metadataGroups,
});

const mapDispatchToProps = dispatch => ({
  loadCustomFields: (silent) => {
    dispatch(fetchCustomFields({}, silent));
  },
  loadMetadataGroups: () => (
    dispatch(fetchMetadataGroups())
  ),
});

AdminCustomFields.propTypes = {
  metadataGroups: PropTypes.array.isRequired,
  customFields: PropTypes.array.isRequired,
  loadMetadataGroups: PropTypes.func.isRequired,
  loadCustomFields: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(AdminCustomFields));
